/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import Msdyn365, { Image, getCatalogId, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Drawer, Module, IModuleProps } from '@msdyn365-commerce-modules/utilities';

import { IWebgroupDirectoryData } from './webgroup-directory.data';
import { IWebgroupDirectoryProps } from './webgroup-directory.props.autogenerated';
import { IWebGroupProduct } from './components/webgroup-record'; //WebGroupRecord
import { flatten, uniq, compact } from 'lodash';
import { generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { IWebGroupData, IWebGroupHeaders } from '../../actions/DataServiceEntities.g';
import { GetWebGroupImage } from '../../utilities/get-images';
import { GetSearchTermFromUrl } from '../../utilities';
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { WebGroupGroup } from './components/webgroup-group';
import { IWebGroupCartLines } from './context/webgroup-cart-context';

export interface IWebgroupDirectoryExtendedProps extends IWebgroupDirectoryProps<IWebgroupDirectoryData> {
    canAddToCart: boolean;
    accordions: JSX.Element[];
    addMultipleToCart: any;
    emptyCartList: () => void;
    loaded: boolean;
    filterApplied: boolean;
}

export interface IWebGroupDirectoryState {
    openDrawerList: string[];
    addToCartList: IWebGroupProduct[];
    sourceWebgroupList: IWebGroupData[];
    webgroupList: IWebGroupData[];
    accordions: JSX.Element[];
    filterGroups: (string | undefined)[];
    loaded: boolean;
    searchTerm: string;
    filterApplied: boolean;
    drawerIterator: number;
    lines: Record<string, IWebGroupCartLines>;
}

const captionValues = {
    CaptionOne: 'ValueOne',
    CaptionTwo: 'ValueTwo',
    CaptionThree: 'ValueThree',
    CaptionFour: 'ValueFour',
    CaptionFive: 'ValueFive',
    CaptionSix: 'ValueSix',
    CaptionSeven: 'ValueSeven',
    CaptionEight: 'ValueEight',
    CaptionNine: 'ValueNine',
    CaptionTen: 'ValueTen'
};

/**
 *
 * WebgroupDirectory component
 * @extends {React.PureComponent<IWebgroupDirectoryProps<IWebgroupDirectoryData>>}
 */
class WebgroupDirectory extends React.PureComponent<IWebgroupDirectoryExtendedProps, IWebGroupDirectoryState> {
    public constructor(props: IWebgroupDirectoryExtendedProps) {
        super(props);

        var newLines: Record<string, IWebGroupCartLines> = {};
        this.props.data.webgroups.result?.forEach(item => {
            if (item.WebGroupHeaders?.WebGroupId) {
                newLines[item.WebGroupHeaders?.WebGroupId] = { headers: item.WebGroupHeaders, lines: [] };
            }
        });

        if (this.props.data.webgroups.result) {
            // remove rare case of duplicate webgroups
            const trackWGs = new Set();

            this.props.data.webgroups.result = this.props.data.webgroups.result?.filter(item => {
                if (trackWGs.has(item.WebGroupHeaders?.WebGroupId)) {
                    return false; // discard duplicate
                } else {
                    trackWGs.add(item.WebGroupHeaders?.WebGroupId);
                    return true; // keep
                }
            });
        }

        this.state = {
            openDrawerList: [],
            addToCartList: [],
            sourceWebgroupList: [],
            webgroupList: [],
            accordions: [],
            filterGroups: [],
            loaded: false,
            drawerIterator: 0,
            searchTerm: '',
            filterApplied: false,
            lines: newLines
        };
        this.updateGroup = this.updateGroup.bind(this);
        this.handleAddWebGroupsToCart = this.handleAddWebGroupsToCart.bind(this);
        this.addAllGroupsToCart = this.addAllGroupsToCart.bind(this);
    }

    private _renderCellData(group: IWebGroupData, image: string): JSX.Element | null {
        return (
            <WebGroupGroup
                setState={this.setState}
                webGroup={group}
                image={image}
                key={group.WebGroupHeaders?.WebGroupId}
                actionContext={this.props.context.actionContext}
                updateGroup={this.updateGroup}
                handleAddWebGroupsToCart={this.handleAddWebGroupsToCart}
                isAuthenticated={this.props.context.actionContext.requestContext.user.isAuthenticated}
                searchTerm={this.state.searchTerm}
            />
        );
    }

    private _createDrawerBody(): void {
        const {
            context: { actionContext }
        } = this.props;
        // const previousAccordions = this.state.accordions;
        const newDrawerList: string[] = [];
        // We use this drawer iterator to force the state to update whenever we need it to.
        // By running this function as we save state, we totally refresh all webgroups. Make sure that the lines are cleared to ensure
        // syncronization between line level selection and the values for those lines and their respective groups here.
        const newDrawerIterator = this.state.drawerIterator + 1;
        const accordion = this.state.webgroupList.map((group: IWebGroupData) => {
            const image = GetWebGroupImage(actionContext, group.WebGroupHeaders?.WebGroupId);
            const placeholder = 'placeholder.png';
            const fallBack =
                this.props.config.fallbackImage?.src ??
                generateImageUrl(placeholder, this.props.context.actionContext.requestContext.apiSettings)?.replace(
                    `/${placeholder}`,
                    placeholder
                );
            const cellData: JSX.Element | null = this._renderCellData(group, image);
            const accordionItemContainer: IModuleProps = {
                moduleProps: this.props,
                className: 'ms-product-webgroup-title'
            };
            // open accordion if there's only one or if it has matching webgroup or product
            let openDrawer = group.WebGroupHeaders?.WebGroupId === this.state.searchTerm;
            if (this.state.searchTerm && !openDrawer) {
                for (const p of group.Products ?? []) {
                    if (p.ProductValues?.ItemId === this.state.searchTerm) {
                        openDrawer = true;
                        break;
                    }
                }
            }
            //If this drawer was previously open we need to keep it open
            if (this.state.openDrawerList.find(item => item === `${group.WebGroupHeaders?.WebGroupId}-${this.state.drawerIterator}`)) {
                openDrawer = true;
            }
            //We are updating the open drawer list.
            if (openDrawer) {
                newDrawerList.push(`${group.WebGroupHeaders?.WebGroupId}-${newDrawerIterator}`);
            }
            return (
                <Module {...accordionItemContainer} key={group.WebGroupHeaders?.WebGroupId}>
                    <span className='title-image' data-id={group.WebGroupHeaders?.WebGroupId}>
                        <Image gridSettings={{}} quality={35} imageFallbackOptimize src={image} fallBackSrc={fallBack} />
                    </span>
                    <Drawer
                        key={`${group.WebGroupHeaders?.WebGroupId}-${this.state.drawerIterator + 1}`}
                        collapseProps={{
                            timeout: 0,
                            isOpen: openDrawer
                        }}
                        className='ms-product-webgroup__drawer'
                        openGlyph='ms-product-webgroup__drawer-open'
                        closeGlyph='ms-product-webgroup__drawer-close'
                        glyphPlacement='end'
                        toggleButtonText={group.WebGroupHeaders?.Header ?? ''}
                        onToggle={this._toggle(group.WebGroupHeaders?.WebGroupId ?? '')}
                    >
                        <div className='ms-product-details'>
                            <span className='ms-product-details'>{cellData}</span>
                        </div>
                    </Drawer>
                </Module>
            );
        });
        this.setState({ accordions: accordion, loaded: true, drawerIterator: newDrawerIterator, openDrawerList: newDrawerList });
    }

    private readonly _toggle = (drawerKey: string) => (): void => {
        if (drawerKey !== '') {
            let newDrawerList = this.state.openDrawerList;
            const drawerIndex = newDrawerList.findIndex(item => item === `${drawerKey}-${this.state.drawerIterator}`);
            if (drawerIndex !== -1) {
                //We found it, so remove it so it can be closed.
                newDrawerList = newDrawerList.filter(item => item !== `${drawerKey}-${this.state.drawerIterator}`);
            } else {
                newDrawerList.push(`${drawerKey}-${this.state.drawerIterator}`);
            }
            this.setState({ openDrawerList: newDrawerList });
        }
    };

    private readonly _emptyCartList = (): void => {
        this.setState({ addToCartList: [] });
    };

    private _getFilterName = (object: IWebGroupHeaders, value: string): string => {
        return Object.keys(object).find(key => (object as any)[`${key}`] === value) || '';
    };

    private _processFilters = (): void => {
        if (msdyn365Commerce.isBrowser) {
            // update webgroups to apply selected filters
            document.addEventListener('FILTERS_UPDATED', (data: any) => {
                if (data.detail.length) {
                    // 1. rmove products that doen't match filters
                    const tempArray: IWebGroupData[] = JSON.parse(JSON.stringify(this.state.sourceWebgroupList));
                    const webGroups: IWebGroupData[] = [];
                    tempArray.forEach(g => {
                        // @ts-ignore
                        data.detail.forEach(filter => {
                            const headerName = this._getFilterName(g.WebGroupHeaders!, filter.FilterName);
                            const products = g.Products?.map(p => {
                                let showProduct = false;
                                for (let i = 0; i < filter.FilterValues.length; i++) {
                                    if (
                                        p.ProductValues &&
                                        (p.ProductValues as any)[(captionValues as any)[`${headerName}`]] === filter.FilterValues[`${i}`]
                                    ) {
                                        showProduct = true;
                                        break;
                                    } else {
                                        showProduct = false;
                                    }
                                }
                                if (showProduct) {
                                    return p;
                                }
                                return;
                            });
                            g.Products = products?.filter(p => p !== undefined) as never;
                        });
                        // 2. Only add groups that have products
                        g.Products?.length && webGroups.push(g);
                    });
                    this.setState({ webgroupList: webGroups, filterApplied: true }, this._createDrawerBody);
                } else {
                    this.setState({ webgroupList: this.state.sourceWebgroupList, filterApplied: false }, this._createDrawerBody);
                }
            });
        }
    };

    private updateGroup(lineData: IWebGroupCartLines): void {
        //this is used by webgroupgroups to update the status of their lines. We use this to add ALL webgroups to cart.
        //Individual webgroups can handle adding themselves to cart.
        if (this.state && this.state.lines) {
            const lineName = lineData.headers.WebGroupId ?? '';
            if (!(lineName in this.state.lines) || lineName === '') {
                return;
            }
            const lines = { ...this.state.lines };
            lines[lineName] = lineData;
            this.setState({ lines });
        }
    }

    private getAllGroups(): IWebGroupCartLines[] {
        const allGroups: IWebGroupCartLines[] = [];
        if (this.state.lines) {
            Object.keys(this.state.lines).forEach(key => {
                allGroups.push(this.state.lines[key]);
            });
        }
        return allGroups;
    }

    public async handleAddWebGroupsToCart(webgroups: IWebGroupCartLines[]): Promise<boolean> {
        if (Msdyn365.isBrowser) {
            window.isAddingToCart = true;
        }
        document.dispatchEvent(new CustomEvent('ADDING_TO_CART'));
        const cartState = this.props.data.cart.result;
        const updateProperties = { newExtensionProperties: [{ Key: 'DidRecalculate', Value: { BooleanValue: undefined } }] };
        if (!cartState) {
            return false;
        }
        const catalogId = getCatalogId(this.props.context.actionContext.requestContext);
        const addToCartProducts: { ProductId: number; CatalogId: number; Quantity: number }[] = [];

        webgroups.forEach(webgroup => {
            webgroup.lines.forEach(line => {
                if (line.quantity > 0 && !line.error) {
                    addToCartProducts.push({ ProductId: line.variant.ProductId, CatalogId: catalogId, Quantity: line.quantity });
                }
            });
        });

        if (cartState.cart.Version) {
            const result = await addCartLinesAsync(
                { callerContext: this.props.context.actionContext },
                cartState.cart.Id,
                addToCartProducts,
                cartState.cart.Version
            );
            if (result) {
                await cartState.updateExtensionProperties(updateProperties);
                cartState.refreshCart({});
                document.dispatchEvent(new CustomEvent('ADDED_TO_CART'));
                //Clear Lines
                //Regardless of whether we added only one webgroup or all of them, we will clear all line quantities.
                //When we re-create DrawerBody, it should totally wipe and recreate the webgroups. This might not result in the quantities being reset
                //TODO: Ensure that the variants that exist on the lines are set to their defaults? If i select a variant on a line on one webgroup,
                //and then add a different webgroup to cart, and then try to add the first variant of the previously modified line to cart, will it select the variant you had changed it to?
                //Maybe because you would need to increase the quantity, you would have to update the line and thus set the variant correctly.
                const newLines = this.state.lines;
                if (newLines) {
                    Object.keys(newLines).forEach(key => {
                        newLines[key].lines.forEach(line => {
                            line.quantity = 0;
                        });
                    });
                }
                this.setState({ lines: newLines }, this._createDrawerBody);
                return true;
            }
        }
        return false;
    }

    private addAllGroupsToCart() {
        this.handleAddWebGroupsToCart(this.getAllGroups());
    }

    public determineCanAddToCart(): boolean {
        const allGroups: IWebGroupCartLines[] = this.getAllGroups();
        return (
            allGroups.some(group => group.lines.some(line => line.quantity > 0)) &&
            !allGroups.some(group => group.lines.some(line => line.error))
        );
    }

    public render(): JSX.Element | null {
        const {
            data: { webgroups },
            context: { actionContext }
        } = this.props;
        this.setState({ searchTerm: GetSearchTermFromUrl(actionContext) });
        if (!this.state.loaded && webgroups?.result) {
            const filteredResult = webgroups?.result.filter(g => !!g.Products?.length);
            this.setState(
                {
                    sourceWebgroupList: filteredResult,
                    webgroupList: filteredResult
                },
                this._createDrawerBody
            );
            const filterGroups = webgroups?.result?.map((g: IWebGroupData) => {
                return [
                    g.WebGroupHeaders?.CaptionOne,
                    g.WebGroupHeaders?.CaptionTwo,
                    g.WebGroupHeaders?.CaptionThree,
                    g.WebGroupHeaders?.CaptionFour,
                    g.WebGroupHeaders?.CaptionFive,
                    g.WebGroupHeaders?.CaptionSix,
                    g.WebGroupHeaders?.CaptionSeven,
                    g.WebGroupHeaders?.CaptionEight,
                    g.WebGroupHeaders?.CaptionNine,
                    g.WebGroupHeaders?.CaptionTen
                ];
            });
            this.setState({ filterGroups: compact(uniq(flatten(filterGroups))) });
            this._processFilters();
        }
        if (webgroups?.status === 'FAILED') {
            this.setState({ loaded: true });
        }
        const viewProps = {
            ...this.props,
            canAddToCart: this.determineCanAddToCart(),
            accordions: this.state.accordions,
            addMultipleToCart: this.addAllGroupsToCart,
            emptyCartList: this._emptyCartList,
            loaded: this.state.loaded,
            filterApplied: this.state.filterApplied
        };
        return this.props.renderView(viewProps);
    }
}

export default WebgroupDirectory;
